import {Component, Input, OnInit, OnDestroy} from '@angular/core';
import {MessageService} from 'primeng/api';
import {
  CruiseControlSelectionCriteriaInterface
} from '../../../../../model/cruise-control-selection-criteria.interface';
import {CruiseControlSettingsInterface} from '../../../../../model/cruise-control-settings.interface';
import {CruiseControlStatusInterface} from '../../../../../model/cruise-control-status.interface';
import {CruiseControlService} from '../../../../../service/cruise-control.service';
import {UserService} from '../../../../../service/user.service';
import {repeat, Subject, takeUntil} from 'rxjs';

@Component({
  selector: 'auto-cruise-control-form',
  templateUrl: './cruise-control-form.component.html',
  styleUrl: './cruise-control-form.component.scss',
  standalone: false
})
export class CruiseControlFormComponent implements OnInit, OnDestroy {
  @Input() accountId: number;
  @Input() settings: CruiseControlSettingsInterface;
  @Input() label: string;
  readonly defaultOfferSetStatusOption: boolean = false;
  readonly defaultCreateInventoryOnlyOfferSetsOption: boolean = true;
  readonly defaultFullVisibilityOfferSetScopeOption: number = 2;
  readonly defaultDealerAddOnOfferSetActiveOption: boolean = false;
  readonly offerSetVisibilityScopeFeatureName: string = 'offerSetVisibilityScope';
  readonly dealerAddOnOfferSetStatusFeatureName: string = 'ccDealerAddOnOfferSetStatus';
  generateAsOptions: object[] = [{value: false, label: 'Active'}, {value: true, label: 'Inactive'}]
  dealerAddOnOfferSetActiveOptions: object[] = [{value: true, label: 'Active'}, {value: false, label: 'Inactive'}]
  generateAllInventoryOptions: object[] = [{value: true, label: 'On'}, {value: false, label: 'Off'}]
  buildByOptions: object[] = [{value: 1, label: 'Model'}, {value: 2, label: 'Trim'}, {value: 3, label: 'Vehicle'}]
  selectionCriteria: CruiseControlSelectionCriteriaInterface[] = [];
  minimumImageOptions: object[] = []
  status: CruiseControlStatusInterface;
  pollingSubject: Subject<any> = new Subject<any>();
  lastCommandCreated: string;
  monitorNewCommand: boolean = false;
  isRunRequestInFlight: boolean = false;
  showRunningButtonState: boolean = false;
  offerSetVisibilityScopeEnabled: boolean = false;
  dealerAddOnOfferSetActiveEnabled: boolean = false;
  readonly cruiseControlStatusQueued: string = 'queued';
  readonly cruiseControlStatusInProgress: string = 'in_progress';
  readonly cruiseControlStatusDone: string = 'done';
  readonly cruiseControlStatusFailed: string = 'failed';
  readonly cruiseControlStatusCancelled: string = 'cancelled';
  constructor(private cruiseControlService: CruiseControlService, private messageService: MessageService, private userService: UserService) {
  }
  /**
   * Init component
   */
  ngOnInit(): void {
    this.normalizeSettings();
    this.populateSelectionCriteria();
    this.populateMinimumImageRequirementOptions();
    this.updateStatus();
    this.checkFeatureFlags();
  }
  /**
   * Cleanup component
   */
  ngOnDestroy(): void {
    this.cancelStatusPolling();
  }
  /**
   * Checks feature flags for the account
   */
  checkFeatureFlags(): void {
    this.userService.getMpopFeature(this.offerSetVisibilityScopeFeatureName).subscribe({
      next: (features) => {
        const feature = features.shift();
        if (feature) {
          this.offerSetVisibilityScopeEnabled = feature.enabled;
        }
      },
      error: () => this.offerSetVisibilityScopeEnabled = false
    });

    this.userService.getMpopFeature(this.dealerAddOnOfferSetStatusFeatureName).subscribe({
      next: (features) => {
        const feature = features.shift()
        if (feature) {
          this.dealerAddOnOfferSetActiveEnabled = feature.enabled;
        }
      },
      error: () => this.dealerAddOnOfferSetActiveEnabled = false
    });
  }
  /**
   * Applies defaults required for segmented controls and dealer add-on setting
   */
  normalizeSettings(): void {
    if (!this.settings) {
      return;
    }

    if (this.settings.generateAsPreview === null || this.settings.generateAsPreview === undefined) {
      this.settings.generateAsPreview = this.defaultOfferSetStatusOption;
    }
    if (this.settings.generateAllInventory === null || this.settings.generateAllInventory === undefined) {
      this.settings.generateAllInventory = this.defaultCreateInventoryOnlyOfferSetsOption;
    }
    if (!this.settings.cruiseControlCreateLevelId) {
      this.settings.cruiseControlCreateLevelId = this.defaultFullVisibilityOfferSetScopeOption;
    }
    if (this.settings.dealerAddOnOfferSetActive === null || this.settings.dealerAddOnOfferSetActive === undefined) {
      this.settings.dealerAddOnOfferSetActive = this.defaultDealerAddOnOfferSetActiveOption;
    }
  }
  /**
   * Populates options for the minimum image requirements form field
   */
  populateMinimumImageRequirementOptions(): void {
    for (let i = 0; i < 21; i++) {
      this.minimumImageOptions.push({ value: i, label: i });
    }
  }
  /**
   * Populates the vehicle selection criteria options for the Vehicle Selection Criteria form field
   */
  populateSelectionCriteria(): void {
    this.cruiseControlService.getSelectionCriteria().subscribe({
      next: (list) => {
        for (const item of list) {
          if (this.settings.vehicleCondition === item.vehicleCondition) {
            this.selectionCriteria.push(item);
          }
        }
      },
      error: () => this.messageService.add({severity: 'error', summary: 'Error', detail: 'Unable to retrieve vehicle selection criteria'})
    });
  }
  /**
   * Updates the Cruise Control command status on page load
   */
  updateStatus(): void {
    this.cruiseControlService.getStatus(this.accountId, this.settings.vehicleCondition).subscribe({
      next: (status) => {
        this.status = status;
        this.lastCommandCreated = status.created;
        if (status.created && !status.completed) {
          this.startStatusPolling();
          this.messageService.add({severity: 'info', detail: `Cruise Control is currently running for ${this.label.toLowerCase()} inventory`});
        }
      },
      error: () => this.messageService.add({severity: 'error', summary: 'Error', detail: 'Unable to retrieve Cruise Control command status'})
    });
  }
  /**
   * Gets the latest command state for status and styling
   * @param status
   */
  getCommandState(status: CruiseControlStatusInterface): string {
    let commandState: string = this.cruiseControlStatusQueued;

    if (status && status.canceled) {
      commandState = this.cruiseControlStatusCancelled;
    }
    if (status && status.completed) {
      commandState = this.cruiseControlStatusDone;
      const parsedStatusResult: any = this.getParsedStatusResult(status);
      const offerSetsCreatedCount: number = this.getOfferSetsCreatedCount(status);
      const hasErrors: boolean = this.hasPopulatedErrors(parsedStatusResult);
      if (offerSetsCreatedCount === 0 && hasErrors) {
        commandState = this.cruiseControlStatusFailed;
      }
      if (status.canceled) {
        commandState = this.cruiseControlStatusCancelled;
      }
    }

    if (!status || !status.completed) {
      if (status && (status.started || this.showRunningButtonState)) {
        commandState = this.cruiseControlStatusInProgress;
      }
      if (status && status.pending) {
        commandState = this.cruiseControlStatusQueued;
      }
    }


    return commandState;
  }
  /**
   * Determines if parsed result payload has a populated errors value
   */
  hasPopulatedErrors(parsedStatusResult: any): boolean {
    let hasErrors: boolean = false;

    if (parsedStatusResult && parsedStatusResult.errors !== null && parsedStatusResult.errors !== undefined) {
      if (Array.isArray(parsedStatusResult.errors)) {
        if (parsedStatusResult.errors.length > 0) {
          hasErrors = true;
        }
      }
      if (!Array.isArray(parsedStatusResult.errors)) {
        if (`${parsedStatusResult.errors}`.trim() !== '') {
          hasErrors = true;
        }
      }
    }


    return hasErrors;
  }
  /**
   * Gets display label for current command status
   */
  getCurrentStatusLabel(): string {
    let statusLabel: string = 'Queued';
    const commandState: string = this.getCommandState(this.status);

    if (commandState === this.cruiseControlStatusInProgress) {
      statusLabel = 'In Progress';
    }
    if (commandState === this.cruiseControlStatusDone) {
      statusLabel = 'Done';
    }
    if (commandState === this.cruiseControlStatusFailed) {
      statusLabel = 'Failed';
    }
    if (commandState === this.cruiseControlStatusCancelled) {
      statusLabel = 'Cancelled';
    }


    return statusLabel;
  }
  /**
   * Gets css class for current status pill
   */
  getCurrentStatusClass(): string {
    let statusClass: string = 'status-pill-queued';
    const commandState: string = this.getCommandState(this.status);

    if (commandState === this.cruiseControlStatusInProgress) {
      statusClass = 'status-pill-in-progress';
    }
    if (commandState === this.cruiseControlStatusDone) {
      statusClass = 'status-pill-done';
    }
    if (commandState === this.cruiseControlStatusFailed) {
      statusClass = 'status-pill-failed';
    }
    if (commandState === this.cruiseControlStatusCancelled) {
      statusClass = 'status-pill-cancelled';
    }


    return statusClass;
  }
  /**
   * Gets class for latest result text
   */
  getLatestResultClass(): string {
    let resultClass: string = 'result-placeholder';
    const commandState: string = this.getCommandState(this.status);

    if (commandState === this.cruiseControlStatusDone) {
      resultClass = 'success-text';
    }
    if (commandState === this.cruiseControlStatusFailed) {
      resultClass = 'error-text';
    }


    return resultClass;
  }
  /**
   * Gets user-facing latest result text
   */
  getLatestResultText(): string {
    let latestResultText: string = '--';
    const commandState: string = this.getCommandState(this.status);

    if (commandState === this.cruiseControlStatusDone) {
      latestResultText = 'All offer sets are up to date.';
      const offerSetsCreatedCount: number = this.getOfferSetsCreatedCount(this.status);
      if (offerSetsCreatedCount >= 0) {
        latestResultText = `${offerSetsCreatedCount} offer sets created`;
      }
    }
    if (commandState === this.cruiseControlStatusFailed) {
      latestResultText = 'Run failed — no changes were applied';
      const parsedStatusResult: any = this.getParsedStatusResult(this.status);
      if (parsedStatusResult && parsedStatusResult.errors) {
        latestResultText = parsedStatusResult.errors;
      }
      if (!parsedStatusResult && this.status && this.status.result) {
        latestResultText = this.status.result;
      }
    }


    return latestResultText;
  }
  /**
   * Parses status result payload when API returns JSON as a string
   */
  getParsedStatusResult(status: CruiseControlStatusInterface): any {
    let parsedStatusResult: any = null;

    if (status && status.result) {
      const trimmedResult: string = status.result.trim();
      if (trimmedResult.startsWith('{')) {
        try {
          parsedStatusResult = JSON.parse(trimmedResult);
        } catch {
          parsedStatusResult = null;
        }
      }
    }


    return parsedStatusResult;
  }
  /**
   * Gets count of created offer sets from status payload
   */
  getOfferSetsCreatedCount(status: CruiseControlStatusInterface): number {
    let offerSetsCreatedCount: number = -1;

    if (status && typeof status.adsCreated === 'number') {
      offerSetsCreatedCount = status.adsCreated;
    }
    if (offerSetsCreatedCount < 0) {
      const parsedStatusResult: any = this.getParsedStatusResult(status);
      if (parsedStatusResult && typeof parsedStatusResult.specialsCreated === 'number') {
        offerSetsCreatedCount = parsedStatusResult.specialsCreated;
      }
      if (parsedStatusResult && typeof parsedStatusResult.adsCreated === 'number') {
        offerSetsCreatedCount = parsedStatusResult.adsCreated;
      }
    }


    return offerSetsCreatedCount;
  }
  /**
   * Gets latest run timestamp
   */
  getLatestRunDate(): string {
    let latestRunDate: string = '';

    if (this.status && this.status.created) {
      latestRunDate = this.status.created;
    }


    return latestRunDate;
  }
  /**
   * Determines if command is actively queueing or running
   */
  isCommandActive(): boolean {
    let isActive: boolean = false;

    if (this.isRunRequestInFlight || this.showRunningButtonState) {
      isActive = true;
    }
    if (this.status && this.status.created && !this.status.completed) {
      isActive = true;
    }


    return isActive;
  }
  /**
   * Gets run button label
   */
  getRunButtonLabel(): string {
    let runButtonLabel: string = 'Run Now';

    if (this.isRunRequestInFlight) {
      runButtonLabel = 'Queuing';
    }
    if (!this.isRunRequestInFlight && this.isCommandActive()) {
      runButtonLabel = 'Running';
    }


    return runButtonLabel;
  }
  /**
   * Determines if run button is disabled
   */
  isRunButtonDisabled(): boolean {
    let isDisabled: boolean = false;

    if (!this.settings || !this.settings.enabled) {
      isDisabled = true;
    }
    if (this.isCommandActive()) {
      isDisabled = true;
    }


    return isDisabled;
  }
  /**
   * Saves the changed settings values
   */
  updateSettings(): void {
    this.normalizeSettings();
    this.cruiseControlService.updateSettings(this.settings).subscribe({
      next: () => this.messageService.add({
        severity: 'success', summary: 'Success', detail: 'Settings were updated'}),
      error: () => this.messageService.add({
        severity: 'error', summary: 'Error', detail: 'Failed to save settings'})
    });
  }
  /**
   * Starts a new Cruise Control command
   */
  startCruiseControl(): void {
    this.isRunRequestInFlight = true;
    this.showRunningButtonState = true;
    this.startStatusPolling();
    this.messageService.add({
      severity: 'info', summary: 'Info', detail: `Starting Cruise Control for ${this.label.toLowerCase()} offer sets`});
    this.cruiseControlService.run(this.accountId, this.settings.vehicleCondition).subscribe({
      next: () => {
        this.isRunRequestInFlight = false;
      },
      error: () => {
        this.isRunRequestInFlight = false;
        this.showRunningButtonState = false;
        this.cancelStatusPolling();
        this.messageService.add({
          severity: 'error', summary: 'Error', detail: 'Cruise Control ran into an issue and could not complete. View status for more details.'});
      }
    });
  }
  /**
   * Polls the status of the most recent Cruise Control command
   */
  startStatusPolling(): void {
    this.cruiseControlService.getStatus(this.accountId, this.settings.vehicleCondition)
      .pipe(
        repeat({delay: 1000}),
        takeUntil(this.pollingSubject)
      )
      .subscribe({
        next: (status) => {
          if (this.lastCommandCreated !== status.created) {
            this.lastCommandCreated = status.created;
            this.monitorNewCommand = true;
          }
          if (this.monitorNewCommand) {
            this.status = status;
            if (status.created && !status.completed) {
              this.showRunningButtonState = false;
            }
            if (status.completed) {
              this.showRunningButtonState = false;
              this.monitorNewCommand = false;
              this.cancelStatusPolling();
              this.messageService.add({
                severity: 'success', summary: 'Success', detail: 'Cruise Control completed'});
            }
          }
        }
      });
  }
  /**
   * Stops polling the status of the most recent Cruise Control command
   */
  cancelStatusPolling(): void {
    this.pollingSubject.complete();
    this.pollingSubject = new Subject<any>();
  }

  /**
   * Checks if the Cruise Control command is in progress
   * @param status
   */
  isInProgress(status: CruiseControlStatusInterface): boolean {
    let result = false;
    if (status) {
      const isRunning = !status.canceled && ((status.started && !status.completed) || status.pending);
      result = isRunning || (status.manualProcess && !!status.created && (!status.completed || !status.canceled));
    }

    return result;
  }
}
